/**
 * MODELO DE VIEWMODEL DE PÁGINA DE CADASTRO.
 */

import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { ContratoFinanceiroForm, ContratoFinanceiroSchema, defaultContratoFinanceiroForm } from "../schemas/ContratoFinanceiro_cadastro/ContratoFinanceiro_form";
import { defaultPgtoContratoFinanceiroForm, PgtoContratoFinanceiroForm, PgtoContratoFinanceiroSchema } from "../schemas/ContratoFinanceiro_cadastro/PgtoContratoFinanceiro_form";
import { useFinanceiroContratosCadastroPagination } from "../hooks/financeiro_contratos_cadastro/pagination";
import { ContratoFinanceiro } from "../../domain/models/contrato_financeiro";
import { ModelPgtoContratoFinanceiroLista } from "../../domain/models/pgto_contrato_financeiro_lista";
import { PgtoContratoFinanceiro } from "../../domain/models/pgto_contrato_financeiro";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { PgtoInformacaoCadastroForm } from "../schemas/ContratoFinanceiro_cadastro/PgtoInformacaoCadastro_form";
import { ValoresContratoFinanceiro } from "../../domain/models/valoresContratoFinanceiro";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { LogReajusteContratoFinanceiro } from "../../domain/models/log_reajuste_contrato_financeiro";
import { useLogReajusteContratoFinanceiroPagination } from "../hooks/financeiro_contratos_cadastro/pagination_log_reajuste_contrato";
import { ReajusteContratoIndividualForm } from "../schemas/ContratoFinanceiro_cadastro/ReajusteContratoIndividual_form";
import { LogContratoFinanceiroDetalhadoModel } from "../../domain/models/log_contrato_financeiro_detalhado";
import { useLogContratoFinanceiroDetalhadoPagination } from "../hooks/financeiro_contratos_cadastro/pagination_log_contrato_detalhado";
import { JurosMultaContratoForm } from "../schemas/ContratoFinanceiro_cadastro/JurosMultaContrato_form";

type LoadPgtoListaState = {
    idContratoFinanceiro: number

    page?: number;
    rowsPerPage?: number;
}

type LoadLogReajusteState = {
    idContratoFinanceiro: number

    page?: number;
    rowsPerPage?: number;
}

export const useFinanceiroContratoCadastroViewModel = () => {
    const {
        reports: {
            getReportContratoFinanceiroIndividualUseCase
        },
        commons: {
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,
        },
        financeiro: {
            getIndiceReajusteDropDownUseCase,
            getContratoFinanceiroByIdUseCase,
            createContratoFinanceiroUseCase,
            updateContratoFinanceiroUseCase,
            deleteContratoFinanceiroUseCase,
            getAllPgtoContratoFinanceiroUseCase,
            getPgtoContratoFinanceiroByIdUseCase,
            createPgtoContratoFinanceiroUseCase,
            updatePgtoContratoFinanceiroUseCase,
            deletePgtoContratoFinanceiroUseCase,
            getAllLogReajusteContratoFinanceiroUseCase,
            createReajusteContratoIndividualUseCase,
            getAllLogContratoFinanceiroDetalhadoUseCase,
            updateMultaJurosContratoUseCase,
        },
    } = useDependencyInjection();

    const navigate = useNavigate();
    const location = useLocation();
    const pagination = useFinanceiroContratosCadastroPagination();
    const paginationLogReajuste = useLogReajusteContratoFinanceiroPagination();
    const paginationLogDetalhado = useLogContratoFinanceiroDetalhadoPagination();

    const [reportViewerState, startReportViewerProcess] = useReportViewer();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const {
        pushErrorNotification,
        pushInfoNotification,
        setPageLoading,
    } = usePageLoader();

    const [contratoFinanceiro, setContratoFinanceiro] = useState<ContratoFinanceiro>();
    const [selectedIdContratoFinanceiro, setSelectedIdContratoFinanceiro] = useState<number | undefined>();
    const [selectedIdPgto, setSelectedIdPgto] = useState<number | undefined>();

    const [logReajusteContratoFinanceiroLista, setLogReajusteContratoFinanceiroLista] = useState<LogReajusteContratoFinanceiro[] | []>([]);
    const [logContratoFinanceiroDetalhadoLista, setLogContratoFinanceiroDetalhadoLista] = useState<LogContratoFinanceiroDetalhadoModel[] | []>([]);

    const [pgtoContratoLista, setPgtoContratoLista] = useState<ModelPgtoContratoFinanceiroLista[]>()
    const [pgtoContrato, setPgtoContrato] = useState<PgtoContratoFinanceiro>()

    const [openDeletePgtoModal, setOpenDeletePgtoModal] = useState(false);
    const [openDeleteContratoModal, setOpenDeleteContratoModal] = useState(false);

    const [openCancelarContratoModal, setOpenCancelarContratoModal] = useState(false);
    const [openConfigurarPgtoModal, setOpenConfigurarPgtoModal] = useState(false);
    const [openLogReajusteContratoModal, setOpenLogReajusteContratoModal] = useState(false);
    const [openLogContratoFinanceiroDetalhadoModal, setOpenLogContratoFinanceiroDetalhadoModal] = useState(false);
    const [openUpdateMotivoModal, setOpenUpdateMotivoModal] = useState(false);
    const [openConfigurarMultaJurosModal, setOpenConfigurarMultaJurosModal] = useState(false);



    const [openInfoPgtosModal, setOpenInfoPgtosModal] = useState(false);
    const [openReajusteIndividualModal, setOpenReajusteIndividualModal] = useState(false);

    const [ddlIndiceReajuste, setDdlIndiceReajuste] = useState<DropDown[]>();
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();
    const [valoresFooter, setValoresFooter] = useState<ValoresContratoFinanceiro | undefined>()

    const hookFormContrato = useForm<ContratoFinanceiroForm>({
        reValidateMode: "onSubmit",
        mode: "onSubmit",
        defaultValues: defaultContratoFinanceiroForm,
        resolver: zodResolver(ContratoFinanceiroSchema),
    });

    const selectedIdCentroCusto = hookFormContrato.watch("idCentroCusto");
    const valorGlobal = hookFormContrato.watch("valorGlobal");
    const valorTotal = hookFormContrato.watch("valorTotal");

    const hookFormPgto = useForm<PgtoContratoFinanceiroForm>({
        reValidateMode: "onSubmit",
        mode: "onSubmit",
        defaultValues: defaultPgtoContratoFinanceiroForm,
        resolver: zodResolver(PgtoContratoFinanceiroSchema),
    });

    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification("Erro", [e]),
    });

    //#region ESTADOS CADASTRO PGTOS
    const [statusContrato, setStatusContrato] = useState<boolean>(true);
    const [valorFinanciado, setValorFinanciado] = useState<number>();
    const [valorRestante, setValorRestante] = useState<number>();

    const [idClienteUnidade] = hookFormContrato.watch(["idCliente"]);


    const [projetarMeses] = hookFormPgto.watch(["projetarMeses"]);
    const [pagamentoAvista] = hookFormPgto.watch(["pagamentoAvista"]);
    const [entrada] = hookFormPgto.watch(["entrada"]);
    const [dividirEntrada] = hookFormPgto.watch(["dividirEntrada"]);
    const [parcelaSecundaria] = hookFormPgto.watch(["parcelaSecundaria"]);


    //#endregion



    //#region CARREGAMENTO

    const loadContratoFinanceiro = useCallback(async (idContrato: number) => {
        try {
            const contrato = await getContratoFinanceiroByIdUseCase.getContratoFinanceiroById(idContrato);
            setContratoFinanceiro(contrato);
            setSelectedIdContratoFinanceiro(idContrato);

            if (contrato.status === 3) {
                setStatusContrato(false)
            }

            setValoresFooter({
                totalFinanciado: contrato.valorTotal ?? null,
                totalSemReajuste: contrato.totalSemReajuste ?? null,
                totalNominal: contrato.totalNominal ?? null,
                totalReceber: contrato.totalReceber ?? null,
                totalJuros: contrato.totalJuros ?? null,
                totalFinal: contrato.totalFinal ?? null,
                totalRecebido: contrato.totalRecebido ?? null
            });

            if (contrato.totalSemReajuste != null && contrato.valorTotal != null) {
                const valorRestante = (contrato.valorTotal - contrato.totalSemReajuste)
                setValorRestante(valorRestante);
            } else {
                setValorRestante(contrato.valorTotal != null ? contrato.valorTotal : 0);
            }

            // popular form
            Object.entries(contrato).forEach(([key, value]) => {
                hookFormContrato.setValue(key as keyof ContratoFinanceiroForm, value);
            });

            const valorNormalizado = contrato.valorTotal != null ? contrato.valorTotal : 0;
            setValorFinanciado(valorNormalizado);
        } catch (e) {
            pushErrorNotification("Erro", processError(e, "Falha ao carregar contrato financeiro!"));
        }
    }, [
        getContratoFinanceiroByIdUseCase,
        hookFormContrato,
        pushErrorNotification
    ]);


    const loadPgtoContrato = useCallback(async (idPgto: number) => {
        try {
            const contrato = await getPgtoContratoFinanceiroByIdUseCase.getPgtoContratoFinanceiroById(idPgto);
            setPgtoContrato(contrato);

        } catch (e) {
            pushErrorNotification("Erro", processError(e, "Falha ao carregar contrato financeiro!"));
        }
    }, [
        getPgtoContratoFinanceiroByIdUseCase,
        pushErrorNotification,
    ]);


    const loadPgtoContratoLista = useCallback(async (funcState?: LoadPgtoListaState) => {
        try {

            setLoading(true);
            const filteredIdContratoFinanceiro = evaluateParamValue(funcState?.idContratoFinanceiro, selectedIdContratoFinanceiro)
            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllPgtoContratoFinanceiroUseCase.getAllPgtoContratoFinanceiro(
                filteredIdContratoFinanceiro ?? 0,
                filteredPage,
                filteredRowsPerPage
            );

            if (getAllPgtoContratoFinanceiroUseCase.items.length > 0) {
                hookFormPgto.setValue('parcelaSecundaria', true);
                hookFormPgto.setValue('entrada', 0);
            }

            setPgtoContratoLista(getAllPgtoContratoFinanceiroUseCase.items);

            pagination.setRowsPerPage(getAllPgtoContratoFinanceiroUseCase.pageSize);
            pagination.setTotalItems(getAllPgtoContratoFinanceiroUseCase.totalItems);

        } catch (e) {
            pushErrorNotification("Erro", processError(e, "Falha ao carregar pagamentos!"));
        } finally {
            setLoading(false);
        }

    }, [
        getAllPgtoContratoFinanceiroUseCase,
        pagination,
        hookFormPgto,
        selectedIdContratoFinanceiro,
        pushErrorNotification,

    ]);


    const loadLogReajusteContratoFinanceiro = useCallback(async (funcState?: LoadLogReajusteState) => {
        try {
            if (selectedIdContratoFinanceiro) {

                setLoading(true);
                const filteredIdContratoFinanceiro = evaluateParamValue(funcState?.idContratoFinanceiro, selectedIdContratoFinanceiro)
                const filteredPage = funcState?.page ?? pagination.page;
                const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

                await getAllLogReajusteContratoFinanceiroUseCase.getAllLogReajusteContratoFinanceiro(
                    filteredIdContratoFinanceiro ?? 0,
                    filteredPage,
                    filteredRowsPerPage);

                setLogReajusteContratoFinanceiroLista(getAllLogReajusteContratoFinanceiroUseCase.items.map(log => ({ ...log, percentualReajuste: log.percentualReajuste != null ? log.percentualReajuste / 100 : 0 })));

                paginationLogReajuste.setRowsPerPage(getAllLogReajusteContratoFinanceiroUseCase.pageSize);
                paginationLogReajuste.setTotalItems(getAllLogReajusteContratoFinanceiroUseCase.totalItems);
            }
        } catch (e) {
            pushErrorNotification("Erro", processError(e, "Falha ao buscar log de reajuste do contrato financeiro!"));
        }
    }, [
        getAllLogReajusteContratoFinanceiroUseCase,
        pagination,
        selectedIdContratoFinanceiro,
        paginationLogReajuste,
        pushErrorNotification
    ]);


    const loadLogContratoFinanceiroDetalhado = useCallback(async (funcState?: LoadLogReajusteState) => {
        try {
            if (selectedIdContratoFinanceiro) {
                setLoading(true);
                const filteredIdContratoFinanceiro = evaluateParamValue(funcState?.idContratoFinanceiro, selectedIdContratoFinanceiro)

                const filteredPage = funcState?.page ?? pagination.page;
                const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

                await getAllLogContratoFinanceiroDetalhadoUseCase.execute(
                    filteredIdContratoFinanceiro ?? 0,
                    filteredPage,
                    filteredRowsPerPage);

                setLogContratoFinanceiroDetalhadoLista(getAllLogContratoFinanceiroDetalhadoUseCase.items);

                paginationLogReajuste.setRowsPerPage(getAllLogContratoFinanceiroDetalhadoUseCase.pageSize);
                paginationLogReajuste.setTotalItems(getAllLogContratoFinanceiroDetalhadoUseCase.totalItems);
            }
        } catch (e) {
            pushErrorNotification("Erro", processError(e, "Falha ao buscar log detalhado do contrato financeiro!"));
        }
    }, [
        getAllLogContratoFinanceiroDetalhadoUseCase,
        pagination,
        selectedIdContratoFinanceiro,
        paginationLogReajuste,
        pushErrorNotification
    ]);

    const loadIndiceReajusteDropDown = useCallback(async () => {
        try {
            const result = await getIndiceReajusteDropDownUseCase.getIndiceReajusteDropDown();
            setDdlIndiceReajuste(result);
        } catch (e) {
            pushErrorNotification("Erro", processError(e, "Falha ao buscar Índices de Reajuste!"));
        }
    }, [
        getIndiceReajusteDropDownUseCase,
        pushErrorNotification
    ]);





    //#endregion






    //#region PAGE START
    usePageStartBootstrap(async () => {
        try {
            setPageLoading(true);
            await Promise.all([
                loadIndiceReajusteDropDown(),
            ]);
            const idContrato = selectedIdContratoFinanceiro ?? location.state?.idFinanceiroContrato as number;
            if (idContrato) {
                await loadContratoFinanceiro(idContrato);
                await loadPgtoContratoLista({ idContratoFinanceiro: idContrato, page: 1, rowsPerPage: 100 })
                setSelectedIdContratoFinanceiro(idContrato)
            } else {
                setStatusContrato(false);
                setValoresFooter(undefined);

            }
        } finally {
            setPageLoading(false);
        }
    });

    //#endregion




    //#region USEEFFECTS


    useEffect(() => {
        if (!contratoFinanceiro && valorGlobal != null && valorTotal != null) {
            const resultado = (valorGlobal - valorTotal)
            hookFormContrato.setValue('valorFinanciadoBanco', resultado);
        }
    }, [valorGlobal, valorTotal, contratoFinanceiro, hookFormContrato]);


    //#endregion



    //#region SALVAR/UPDATE

    const handleSalvarContrato = useCallback(async (form: ContratoFinanceiroForm) => {
        try {
            setSaving(true);
            setLoading(true);

            if (!form.valorTotal || form.valorTotal <= 0) {
                pushErrorNotification("Atenção", "O valor total do contrato não pode ser menor ou igual a zero!");
                setSaving(false);
                setLoading(false);
                return;
            }

            if (selectedIdContratoFinanceiro) {
                await updateContratoFinanceiroUseCase.updateContratoFinanceiro({
                    idContratoFinanceiro: selectedIdContratoFinanceiro,
                    idCentroCusto: form.idCentroCusto,
                    idCliente: form.idCliente,
                    idIndiceReajuste: form.idIndiceReajuste,
                    idFornecedor: form.idFornecedor,
                    idClienteCentroCusto: form.idClienteCentroCusto,

                    descricao: form.descricao,
                    dataCadastro: form.dataCadastro,
                    dataInicio: form.dataInicio,
                    dataTermino: form.dataTermino,
                    dataInicialReajuste: form.dataInicialReajuste,

                    percentualJuros: form.percentualJuros,
                    valorGlobal: form.valorGlobal,
                    valorTotal: form.valorTotal,
                    valorVenda: form.valorVenda,
                    valorFinanciadoBanco: form.valorFinanciadoBanco,
                    valorSaldoDevedor: form.valorSaldoDevedor,
                    valorComissao: form.valorComissao,
                    motivo: form.motivo

                });
                await loadContratoFinanceiro(selectedIdContratoFinanceiro);
                await loadPgtoContratoLista({ idContratoFinanceiro: selectedIdContratoFinanceiro });

                hookFormContrato.setValue('motivo', null);
                setOpenUpdateMotivoModal(false);

                pushInfoNotification("Sucesso", "Contrato atualizado com sucesso!", { isAutoFinish: true });
            } else {
                const idContrato = await createContratoFinanceiroUseCase.createContratoFinanceiro({
                    idContratoFinanceiro: 0,
                    idCentroCusto: form.idCentroCusto,
                    idCliente: form.idCliente,
                    idIndiceReajuste: form.idIndiceReajuste,
                    idFornecedor: form.idFornecedor,
                    idClienteCentroCusto: form.idClienteCentroCusto,

                    descricao: form.descricao,
                    dataCadastro: form.dataCadastro,
                    dataInicio: form.dataInicio,
                    dataTermino: form.dataTermino,
                    dataInicialReajuste: form.dataInicialReajuste,

                    percentualJuros: form.percentualJuros,
                    valorGlobal: form.valorGlobal,
                    valorTotal: form.valorTotal,
                    valorVenda: form.valorVenda,
                    valorFinanciadoBanco: form.valorFinanciadoBanco,
                    valorSaldoDevedor: form.valorSaldoDevedor,
                    valorComissao: form.valorComissao
                });
                setSelectedIdContratoFinanceiro(idContrato);
                await loadContratoFinanceiro(idContrato);
                await loadPgtoContratoLista({ idContratoFinanceiro: idContrato });

                navigate(location.pathname, { state: { idFinanceiroContrato: idContrato }, replace: true });

                pushInfoNotification("Sucesso", "Contrato criado com sucesso!", { isAutoFinish: true });
            }
        } catch (e) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar contrato!"));
        } finally {
            setOpenUpdateMotivoModal(false);
            setSaving(false);
            setLoading(false);
        }
    }, [
        selectedIdContratoFinanceiro,
        createContratoFinanceiroUseCase,
        updateContratoFinanceiroUseCase,
        hookFormContrato,
        location,
        navigate,
        loadContratoFinanceiro,
        loadPgtoContratoLista,
        pushInfoNotification,
        pushErrorNotification

    ]);

    const handleUpdateMotivoModal = useCallback((openState: boolean) => {
        setOpenUpdateMotivoModal(openState);
    }, []);

    //#endregion






    //#region  CONTRATO  INICIAR/CANCELAR/FINALIZAR

    const handleCancelarContratoOpenState = useCallback((openState: boolean) => {
        setOpenCancelarContratoModal(openState);

    }, []);


    const handleCancelarContrato = useCallback(async () => {
        try {
            setSaving(true);
            if (selectedIdContratoFinanceiro) {
                await updateContratoFinanceiroUseCase.updateContratoFinanceiro({
                    idContratoFinanceiro: selectedIdContratoFinanceiro,
                    status: 2
                });
                await loadContratoFinanceiro(selectedIdContratoFinanceiro);
                await loadPgtoContratoLista({ idContratoFinanceiro: selectedIdContratoFinanceiro });
                pushInfoNotification("Sucesso", "Contrato cancelado com sucesso!", { isAutoFinish: true });

            }

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao cancelar contrato!"));
        } finally {
            setSaving(false);
            setOpenCancelarContratoModal(false);
        }
    }, [selectedIdContratoFinanceiro,
        updateContratoFinanceiroUseCase,
        loadPgtoContratoLista,
        loadContratoFinanceiro,
        pushInfoNotification,
        pushErrorNotification
    ]);


    const handleFinalizarContrato = useCallback(async () => {
        try {
            setSaving(true);
            if (selectedIdContratoFinanceiro) {
                await updateContratoFinanceiroUseCase.updateContratoFinanceiro({
                    idContratoFinanceiro: selectedIdContratoFinanceiro,
                    status: 1

                });
                setStatusContrato(false);
                await loadContratoFinanceiro(selectedIdContratoFinanceiro);

                pushInfoNotification("Sucesso", "Contrato Finalizado com sucesso!", { isAutoFinish: true });
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao finalizar contrato!"));
        } finally {
            setSaving(false);
        }
    }, [
        selectedIdContratoFinanceiro,
        updateContratoFinanceiroUseCase,
        loadContratoFinanceiro,
        pushInfoNotification,
        pushErrorNotification]);


    const handleIniciarPagamento = useCallback(async () => {
        try {
            setSaving(true);
            if (selectedIdContratoFinanceiro) {
                await updateContratoFinanceiroUseCase.updateContratoFinanceiro({
                    idContratoFinanceiro: selectedIdContratoFinanceiro,
                    status: 0

                });
                setStatusContrato(true);
                await loadContratoFinanceiro(selectedIdContratoFinanceiro);
                pushInfoNotification("Sucesso", "Contrato Iniciado!", { isAutoFinish: true });
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao iniciar contrato!"));
        } finally {
            setSaving(false);
        }

    }, [selectedIdContratoFinanceiro, updateContratoFinanceiroUseCase, loadContratoFinanceiro, pushInfoNotification, pushErrorNotification]);


    //#endregion






    //#region INFORMACAO PAGAMENTO FUNÇÕES

    const handleConfirmDeletePgto = useCallback(async () => {
        try {
            setSaving(true);
            if (selectedIdPgto) {
                await deletePgtoContratoFinanceiroUseCase.deletePgtoContratoFinanceiro(selectedIdPgto);
                pushInfoNotification("Sucesso", "Pagamentos deletado com sucesso!", { isAutoFinish: true });
            } else {
                if (rowSelectionModel) {
                    const idPgtos: number[] = [];

                    for (let row of rowSelectionModel.ids.values()) {
                        if (typeof row === 'number') {
                            idPgtos.push(row);
                        } else if (typeof row === 'string') {
                            try {
                                idPgtos.push(Number(row));
                            } catch (e) {
                            }
                        }
                    }
                    await Promise.all(
                        idPgtos.map(id =>
                            deletePgtoContratoFinanceiroUseCase.deletePgtoContratoFinanceiro(id)
                        )
                    );
                    pushInfoNotification("Sucesso", "Pagamentos deletados com sucesso!", { isAutoFinish: true });
                }
            }

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Deletar Pagamentos!"));
        } finally {
            setSaving(false);
            setOpenDeletePgtoModal(false)
            await loadContratoFinanceiro(selectedIdContratoFinanceiro ?? 0);
            await loadPgtoContratoLista({ idContratoFinanceiro: selectedIdContratoFinanceiro ?? 0 });
            setRowSelectionModel({ type: 'include', ids: new Set() });
        }
    }, [
        selectedIdPgto,
        deletePgtoContratoFinanceiroUseCase,
        rowSelectionModel,
        selectedIdContratoFinanceiro,
        loadContratoFinanceiro,
        loadPgtoContratoLista,
        pushInfoNotification,
        pushErrorNotification
    ]);


    const handleDeleteInformacaoOpenState = useCallback((idPgto: number | null, openState: boolean) => {
        setOpenDeletePgtoModal(openState)
        if (openState && idPgto) {
            setSelectedIdPgto(idPgto)
        } else {
            setSelectedIdPgto(undefined)
        }
    }, []);




    const handleOpenInformacaoPgtoModal = useCallback(async (idPgto: number | null, openState: boolean) => {
        setOpenInfoPgtosModal(openState);

        try {
            if (openState && idPgto) {
                await loadPgtoContrato(idPgto);
            } else {
                setPgtoContrato(undefined);
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao cadastrar pagamentos!"));
        }
    }, [
        loadPgtoContrato, pushErrorNotification
    ]);


    const handleSubmitInformacaoPgto = useCallback(async (form: PgtoInformacaoCadastroForm) => {
        try {

            setSaving(true);
            if (form.idPgtoContratoFinanceiro && selectedIdContratoFinanceiro) {

                await updatePgtoContratoFinanceiroUseCase.updatePgtoContratoFinanceiro(
                    form.idPgtoContratoFinanceiro,
                    form.observacao,
                    form.valorReceber,
                    form.dataReceber ?? undefined,
                    form.tipo
                )
                setOpenInfoPgtosModal(false);
                pushInfoNotification("Sucesso", "Pagamento Alterado com sucesso!", { isAutoFinish: true });
                await loadPgtoContratoLista({ idContratoFinanceiro: selectedIdContratoFinanceiro });
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao alterar pagamento!"));
        } finally {
            setSelectedIdPgto(undefined)
            setPgtoContrato(undefined)
            setSaving(false);
        }
    }, [
        selectedIdContratoFinanceiro,
        updatePgtoContratoFinanceiroUseCase,
        loadPgtoContratoLista,
        pushInfoNotification,
        pushErrorNotification,

    ]);


    //#endregion


    //#region CONFIGURAR PAGAMENTOS FUNÇÕES


    const handleConfigurarPgtoModal = useCallback((openState: boolean) => {
        setOpenConfigurarPgtoModal(openState)
        if (openState && valorRestante) {
            setValorRestante(valorRestante);
            hookFormPgto.setValue("valorAParcelar", valorRestante);
        }
    }, [valorRestante, hookFormPgto]);

    const handleSubmitPgtosContrato = useCallback(async (form: PgtoContratoFinanceiroForm) => {
        try {

            setSaving(true);
            if (selectedIdContratoFinanceiro && contratoFinanceiro && valorFinanciado) {
                const valorAparcelarNormalizado = form.valorAParcelar != null ? form.valorAParcelar : 0;
                const valorEntradaNormalizado = form.valorEntrada != null ? form.valorEntrada : 0;

                if (!form.pagamentoAvista) {
                    if (valorAparcelarNormalizado === 0 && valorEntradaNormalizado === 0) {
                        pushErrorNotification("Erro", "Os valores de entrada e a parcelar não podem ser zero!!");
                        return;
                    };

                    const valorRestante = valorFinanciado - valorEntradaNormalizado;

                    if (valorAparcelarNormalizado > valorRestante) {
                        pushErrorNotification("Erro", "O valor a parcelar não pode ser maior que o valor restante!!");
                        return;
                    };

                    if (valorRestante == 0 || valorRestante < 0) {
                        pushErrorNotification("Erro", "O contrato já atingiu o valor total disponível!!");
                        return;
                    };
                }

                await createPgtoContratoFinanceiroUseCase.createPgtoContratoFinanceiro({
                    idContratoFinanceiro: selectedIdContratoFinanceiro,
                    valorEntrada: form.valorEntrada,
                    numeroParcelasEntrada: form.numeroParcelasEntrada,
                    numeroParcelas: form.numeroParcelas,
                    dataPrimeiroPgto: form.dataPrimeiroPgto,
                    dataPgtoEntrada: form.dataPgtoEntrada,
                    intervaloDias: form.intervaloDias,
                    observacao: form.observacao,

                    valorAParcelar: form.valorAParcelar,
                    percentualJuros: contratoFinanceiro.percentualJuros,
                    idIndiceReajuste: contratoFinanceiro.idIndiceReajuste,

                    dividirEntrada: form.dividirEntrada,
                    entrada: form.entrada,
                    pagamentoAvista: form.pagamentoAvista,
                    projetarMeses: form.projetarMeses,
                })

                setOpenConfigurarPgtoModal(false);
                hookFormPgto.reset();
                await loadContratoFinanceiro(selectedIdContratoFinanceiro);
                await loadPgtoContratoLista({ idContratoFinanceiro: selectedIdContratoFinanceiro });
                pushInfoNotification("Sucesso", "Pagamentos Criados!", { isAutoFinish: true });
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao cadastrar pagamentos!"));
        } finally {
            setSaving(false);
        }
    }, [
        selectedIdContratoFinanceiro,
        contratoFinanceiro,
        valorFinanciado,
        createPgtoContratoFinanceiroUseCase,
        hookFormPgto,
        loadContratoFinanceiro,
        loadPgtoContratoLista,
        pushInfoNotification,
        pushErrorNotification,

    ]);

    //#endregion



    //#region Relatório Contrato Individual
    const handleContratoFinanceiroRelatorioIndividual = useCallback(async () => {
        try {
            setLoading(true);
            if (!selectedIdContratoFinanceiro) {
                pushErrorNotification("Erro", "Contrato Financeiro não selecionado!");
                return;
            }
            await startReportViewerProcess({
                fetchCallback: async (mimeType) => {
                    return await getReportContratoFinanceiroIndividualUseCase
                        .getReportContratoFinanceiroIndividual(selectedIdContratoFinanceiro, mimeType);
                }
            });

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatório de Contrato Financeiro!'));
        } finally {
            setLoading(false);
        }
    }, [
        getReportContratoFinanceiroIndividualUseCase,
        selectedIdContratoFinanceiro,
        startReportViewerProcess,
        pushErrorNotification,
    ]);

    //#endregion




    //#region JUROS E MULTA FUNÇÕES
    const handleConfigurarMultaJurosModal = useCallback((openState: boolean) => {
        setOpenConfigurarMultaJurosModal(openState)
    }, []);


    const handleSubmitJurosMultaContrato = useCallback(async (form: JurosMultaContratoForm) => {
        try {

            setSaving(true);
            if (selectedIdContratoFinanceiro) {
                await updateMultaJurosContratoUseCase.execute(
                    selectedIdContratoFinanceiro,
                    form.percentualJuros ?? 0,
                    form.percentualMulta ?? 0,
                )

                setOpenConfigurarMultaJurosModal(false);
                pushInfoNotification("Sucesso", "Juros e multa alterados com sucesso!", { isAutoFinish: true });
                await loadPgtoContratoLista({ idContratoFinanceiro: selectedIdContratoFinanceiro });
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao alterar juros e multa!"));
        } finally {
            setSelectedIdPgto(undefined)
            setPgtoContrato(undefined)
            setSaving(false);
        }
    }, [
        selectedIdContratoFinanceiro,
        updateMultaJurosContratoUseCase,
        loadPgtoContratoLista,
        pushInfoNotification,
        pushErrorNotification,

    ]);
    //#endregion




    //#region DELETAR
    const handlePgtoDeleteOpenState = useCallback((idPgto: number | null, openState: boolean) => {
        if (openState && idPgto) {
            setOpenDeletePgtoModal(openState)
        } else {
            setOpenDeletePgtoModal(openState)
        }
    }, []);

    const handleContratoDeleteOpenState = useCallback((openState: boolean) => {
        setOpenDeleteContratoModal(openState)

    }, []);

    const handleDeleteContrato = useCallback(async () => {
        try {
            if (!selectedIdContratoFinanceiro) return;
            await deleteContratoFinanceiroUseCase.deleteContratoFinanceiro(selectedIdContratoFinanceiro);
            pushInfoNotification("Sucesso", "Contrato deletado com sucesso!");
            navigate("/FinanceiroContratos_lista");
        } catch (e) {
            pushErrorNotification("Erro", processError(e, "Falha ao deletar contrato!"));
        }
    }, [selectedIdContratoFinanceiro, deleteContratoFinanceiroUseCase, pushInfoNotification, pushErrorNotification, navigate]);




    //#endregion





    //#region CABEÇALHO

    const handlePageBack = useCallback(() => {
        navigate("/FinanceiroContratos_lista");
    }, [navigate]);

    const handleNewContrato = useCallback(() => {

        hookFormContrato.reset();
        hookFormPgto.reset();
        setSelectedIdContratoFinanceiro(undefined);
        setPgtoContrato(undefined);
        setPgtoContratoLista(undefined);
        setContratoFinanceiro(undefined);
        setStatusContrato(false);
        setValoresFooter(undefined);

    }, [hookFormContrato, hookFormPgto]);



    //#endregion





    //#region REAJUSTE INDIVIDUAL 
    const handleReajusteIndividualOpenState = useCallback((openState: boolean) => {
        setOpenReajusteIndividualModal(openState)

    }, []);


    const handleReajusteIndividualSubmit = useCallback(async (form: ReajusteContratoIndividualForm) => {
        try {
            setSaving(true);
            if (!selectedIdContratoFinanceiro) return;
            await createReajusteContratoIndividualUseCase.execute(
                selectedIdContratoFinanceiro,
                form.idIndiceReajuste ?? 0,
                form.aplicarNaEntrada === 1 ? true : false,
                form.dataInicioAplicacaoReajuste ?? undefined,
                form.dataReajusteDe ?? undefined,
                form.dataReajusteAte ?? undefined,
            );
            pushInfoNotification("Sucesso", "Reajuste aplicado com sucesso!", { isAutoFinish: true });
            await loadContratoFinanceiro(selectedIdContratoFinanceiro);
            await loadPgtoContratoLista({ idContratoFinanceiro: selectedIdContratoFinanceiro, page: 1, rowsPerPage: 25 });
        } catch (e) {
            pushErrorNotification("Erro", processError(e, "Falha ao reajustar contrato!"));
        } finally {
            setOpenReajusteIndividualModal(false);
            setSaving(false);
        }

    }, [
        selectedIdContratoFinanceiro,
        createReajusteContratoIndividualUseCase,
        loadContratoFinanceiro,
        loadPgtoContratoLista,
        pushInfoNotification,
        pushErrorNotification]);


    //#endregion



    //#region TABELA LOG REAJUSTE CONTRATO FINANCEIRO
    const handleLogReajusteContratoOpenState = useCallback(async (openState: boolean) => {
        setOpenLogReajusteContratoModal(openState);
        try {
            setLoading(true);
            if (openState && selectedIdContratoFinanceiro) {
                await loadLogReajusteContratoFinanceiro({ idContratoFinanceiro: selectedIdContratoFinanceiro, page: 1, rowsPerPage: 25 });
            } else {
                setLogReajusteContratoFinanceiroLista([]);
            }
        }
        catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar log de reajuste do contrato financeiro!'));
        } finally {
            setLoading(false);
        }
    }, [selectedIdContratoFinanceiro, loadLogReajusteContratoFinanceiro, pushErrorNotification]);



    const handlePageChangeLogReajuste = useCallback(async (page: number) => {
        try {
            setLoading(true);
            paginationLogReajuste.setPage(page + 1);
            if (selectedIdContratoFinanceiro) {
                await loadLogReajusteContratoFinanceiro({ idContratoFinanceiro: selectedIdContratoFinanceiro, page })
            };
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar log de reajuste!'));
        } finally {
            setLoading(false);
        }
    }, [paginationLogReajuste,
        selectedIdContratoFinanceiro,
        loadLogReajusteContratoFinanceiro,
        pushErrorNotification]);

    const handleRowsPerPageChangeLogReajuste = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            paginationLogReajuste.setPage(1);
            paginationLogReajuste.setRowsPerPage(rowsPerPage);
            if (selectedIdContratoFinanceiro) {
                await loadLogReajusteContratoFinanceiro({ idContratoFinanceiro: selectedIdContratoFinanceiro, rowsPerPage })
            }
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar log de reajuste!'));
        } finally {
            setLoading(false);
        }
    }, [paginationLogReajuste,
        selectedIdContratoFinanceiro,
        loadLogReajusteContratoFinanceiro,
        pushErrorNotification
    ]);

    //#endregion


    //#region PRALISAR CONTRATO


    const handleParalisarContrato = useCallback(async () => {
        try {
            setSaving(true);
            if (selectedIdContratoFinanceiro) {
                await updateContratoFinanceiroUseCase.updateContratoFinanceiro({
                    idContratoFinanceiro: selectedIdContratoFinanceiro,
                    status: 3
                });

                await loadContratoFinanceiro(selectedIdContratoFinanceiro ?? 0);
            } else {
                pushErrorNotification("Erro", "Selecione um contrato para paralisar!");
            }

            pushInfoNotification("Sucesso", "Contratos paralisado com sucesso!", { isAutoFinish: true });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao alterar contratos!"));
        } finally {
            setSaving(false);
            setRowSelectionModel({ type: 'include', ids: new Set() });
        }
    }, [
        selectedIdContratoFinanceiro,
        updateContratoFinanceiroUseCase,
        loadContratoFinanceiro,
        pushInfoNotification,
        pushErrorNotification
    ]);


    //#endregion




    //#region LOG CONTRATO DETALHADO
    const handleLogContratoFinanceiroDetalhadoOpenState = useCallback(async (openState: boolean) => {
        setOpenLogContratoFinanceiroDetalhadoModal(openState);
        try {
            setLoading(true);
            if (openState && selectedIdContratoFinanceiro) {
                await loadLogContratoFinanceiroDetalhado({ idContratoFinanceiro: selectedIdContratoFinanceiro, page: 1, rowsPerPage: 25 });
            } else {
                setLogContratoFinanceiroDetalhadoLista([]);
            }
        }
        catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar log de contrato detalhado!'));
        } finally {
            setLoading(false);
        }
    }, [selectedIdContratoFinanceiro, loadLogContratoFinanceiroDetalhado, pushErrorNotification]);



    const handlePageChangeLogContratoFinanceiroDetalhado = useCallback(async (page: number) => {
        try {
            setLoading(true);
            paginationLogDetalhado.setPage(page + 1);
            if (selectedIdContratoFinanceiro) {
                await loadLogContratoFinanceiroDetalhado({ idContratoFinanceiro: selectedIdContratoFinanceiro, page })
            };
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar log de contrato detalhado!'));
        } finally {
            setLoading(false);
        }
    }, [paginationLogDetalhado,
        selectedIdContratoFinanceiro,
        loadLogContratoFinanceiroDetalhado,
        pushErrorNotification]);

    const handleRowsPerPageChangeLogContratoFinanceiroDetalhado = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            paginationLogDetalhado.setPage(1);
            paginationLogDetalhado.setRowsPerPage(rowsPerPage);
            if (selectedIdContratoFinanceiro) {
                await loadLogContratoFinanceiroDetalhado({ idContratoFinanceiro: selectedIdContratoFinanceiro, rowsPerPage })
            }
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar log de contrato detalhado!'));
        } finally {
            setLoading(false);
        }
    }, [
        paginationLogDetalhado,
        selectedIdContratoFinanceiro,
        loadLogContratoFinanceiroDetalhado,
        pushErrorNotification
    ]);

    //#endregion



    //#region TABELA PAGAMENTOS CONTRATOS
    const handleRowSelectionModelChange = useCallback((rows: GridRowSelectionModel) => {
        setRowSelectionModel(rows);
    }, []);

    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            if (selectedIdContratoFinanceiro) {
                await loadPgtoContratoLista({ idContratoFinanceiro: selectedIdContratoFinanceiro, page })
            };
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de pagamentos!'));
        } finally {
            setLoading(false);
        }
    }, [pagination,
        selectedIdContratoFinanceiro,
        loadPgtoContratoLista,
        pushErrorNotification]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            if (selectedIdContratoFinanceiro) {
                await loadPgtoContratoLista({ idContratoFinanceiro: selectedIdContratoFinanceiro, rowsPerPage })
            }
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de pagamentos!'));
        } finally {
            setLoading(false);
        }
    }, [pagination,
        selectedIdContratoFinanceiro,
        loadPgtoContratoLista,
        pushErrorNotification
    ]);

    //#endregion

    const handleOpenChangeHistory = useCallback((
        idTabela: number | null,
        tipoTabela: LogTipoTabela | null,
    ) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            if (true) {
                changeHistory.handleOpenChangeHistory(selectedIdContratoFinanceiro ?? 0, LogTipoTabela.CONTRATO_FINANCEIRO, true);
            }
        }
    }, [
        selectedIdContratoFinanceiro,
        changeHistory,
    ]);

    return {
        loading,
        saving,
        contratoFinanceiro,
        pgtoContratoLista,
        selectedIdContratoFinanceiro,
        idClienteUnidade,
        statusContrato,
        selectedIdCentroCusto,
        ddlIndiceReajuste: ddlIndiceReajuste ?? [],
        hookFormContrato,
        hookFormPgto,
        openDeleteContratoModal,
        openDeletePgtoModal,
        openConfigurarPgtoModal,
        openInfoPgtosModal,
        pagination,
        rowSelectionModel,
        changeHistory,
        openCancelarContratoModal,
        valorFinanciado,
        valorRestante,
        pgtoContrato,
        selectedIdPgto,
        valoresFooter,
        projetarMeses,
        pagamentoAvista,
        entrada,
        dividirEntrada,
        parcelaSecundaria,
        reportViewerState,
        paginationLogReajuste,
        logReajusteContratoFinanceiroLista,
        openLogReajusteContratoModal,
        openUpdateMotivoModal,
        openReajusteIndividualModal,
        openLogContratoFinanceiroDetalhadoModal,
        logContratoFinanceiroDetalhadoLista,
        paginationLogDetalhado,
        valorTotal,
        valorGlobal,
        openConfigurarMultaJurosModal,

        handleConfigurarMultaJurosModal,
        handleSubmitJurosMultaContrato,
        handleContratoFinanceiroRelatorioIndividual,
        handleParalisarContrato,
        handleReajusteIndividualSubmit,
        handleReajusteIndividualOpenState,
        handleLogContratoFinanceiroDetalhadoOpenState,
        handlePageChangeLogContratoFinanceiroDetalhado,
        handleRowsPerPageChangeLogContratoFinanceiroDetalhado,
        handleUpdateMotivoModal,
        handlePageChangeLogReajuste,
        handleRowsPerPageChangeLogReajuste,
        handleLogReajusteContratoOpenState,
        handleCancelarContratoOpenState,
        handleSubmitPgtosContrato,
        handleSubmitInformacaoPgto,
        handleOpenInformacaoPgtoModal,
        handleConfirmDeletePgto,
        handleDeleteInformacaoOpenState,
        handleFinalizarContrato,
        handleIniciarPagamento,
        handleConfigurarPgtoModal,
        handleSalvarContrato: hookFormContrato.handleSubmit(handleSalvarContrato),
        handleCancelarContrato,
        handlePageBack,
        handleNewContrato,
        handlePageChange,
        handleRowsPerPageChange,
        handleRowSelectionModelChange,
        handleDeleteContrato,
        handlePgtoDeleteOpenState,
        handleContratoDeleteOpenState,
        setOpenInfoPgtosModal,
        handleOpenChangeHistory,

    };
};

